/**
 * ProductCardNew — New design-system product card.
 * Maps to the product card pattern in index.html (POPULAR RIGHT NOW grid).
 *
 * Badge priority: BEST SELLER > SALE > NEW IN (at most one badge shown).
 * Quick Add is wired to the existing addToCart helper — no parallel cart logic.
 *
 * Design tokens: ds-primary (brand accent, Quick Add bg), ds-best-seller (amber badge),
 * ds-error (sale badge), ds-secondary (grey text), ds-on-primary (white),
 * barlow (headline), inter (body/labels).
 *
 * Props:
 *   product: object  — full product document from API
 *   onAddToCart: fn  — called after successful add (to refresh cart count)
 */
import { useContext } from "react";
import { FaShoppingCart, FaStar, FaStarHalfAlt } from "react-icons/fa";
import { Link } from "react-router-dom";
import Context from "../context";
import addToCart from "../helpers/addToCart";
import displayNPRCurrency from "../helpers/displayCurrency";
import scrollTop from "../helpers/scrollTop";
import WishlistButton from "./WishlistButton";

/* ─── Badge helpers ──────────────────────────────────────────────────── */
const isSaleProduct = (p) => p.sellingPrice < p.price;

const Badge = ({ product }) => {
  if (product.isBestSeller) {
    return (
      <span className="absolute top-3 left-3 px-2 py-1 bg-ds-best-seller text-black font-inter text-[10px] font-bold uppercase rounded-sm shadow-sm">
        BEST SELLER
      </span>
    );
  }
  if (isSaleProduct(product)) {
    return (
      <span className="absolute top-3 left-3 px-2 py-1 bg-ds-error text-white font-inter text-[10px] font-bold uppercase rounded-sm shadow-sm">
        SALE
      </span>
    );
  }
  if (product.isNewIn) {
    return (
      <span className="absolute top-3 left-3 px-2 py-1 bg-ds-on-surface text-white font-inter text-[10px] font-bold uppercase rounded-sm shadow-sm">
        NEW IN
      </span>
    );
  }
  return null;
};

/* ─── Star rating display ────────────────────────────────────────────── */
const StarRating = ({ rating = 0, count = 0 }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;

  return (
    <div className="flex items-center gap-1 mb-2">
      <div
        className="flex text-ds-primary"
        aria-label={`${rating} out of 5 stars`}
      >
        {Array.from({ length: full }, (_, i) => (
          <FaStar key={`f${i}`} className="text-[13px]" />
        ))}
        {half === 1 && <FaStarHalfAlt className="text-[13px]" />}
        {Array.from({ length: empty }, (_, i) => (
          <FaStar key={`e${i}`} className="text-[13px] opacity-25" />
        ))}
      </div>
      {count > 0 && (
        <span className="font-inter text-[12px] text-ds-secondary">
          ({count})
        </span>
      )}
    </div>
  );
};

/* ─── Main component ─────────────────────────────────────────────────── */
const ProductCardNew = ({ product, onAddToCart }) => {
  const { fetchUserAddToCart } = useContext(Context);

  const handleAddToCart = async (e) => {
    // Delegates entirely to the existing addToCart helper — no parallel cart logic.
    await addToCart(e, product._id);
    fetchUserAddToCart();
    if (typeof onAddToCart === "function") onAddToCart();
  };

  const onSale = isSaleProduct(product);
  // TODO: Replace with production motorcycle photography
  // Current: productImage[0] from database (Cloudinary URL)
  const imageUrl = product.productImage?.[0] ?? "";

  return (
    <div className="group bg-transparent">
      {/* ── Image area ── */}
      <div className="relative h-[260px] bg-white overflow-hidden mb-3">
        <Link
          to={`/product/${product._id}`}
          onClick={scrollTop}
          aria-label={product.productName}
        >
          <img
            src={imageUrl}
            alt={product.productName}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            loading="lazy"
          />
        </Link>

        {/* Badge — at most one, priority: bestSeller > sale > newIn */}
        <Badge product={product} />

        {/* Wishlist button — top right, fade in on hover */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <WishlistButton
            productId={product._id}
            className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-sm border border-surface-border hover:bg-white"
          />
        </div>

        {/* Quick Add — slides up from bottom on hover */}
        <button
          onClick={handleAddToCart}
          className="absolute bottom-0 left-0 w-full h-[40px] bg-ds-primary hover:bg-ds-primary-hover text-white font-inter text-[13px] font-semibold transition-all duration-300 transform translate-y-full group-hover:translate-y-0 flex items-center justify-center gap-2"
          aria-label={`Quick add ${product.productName} to cart`}
        >
          <FaShoppingCart className="text-[14px]" aria-hidden="true" />
          QUICK ADD
        </button>
      </div>

      {/* ── Content area ── */}
      <div>
        <p className="font-inter font-semibold text-[11px] text-ds-primary uppercase mb-1 tracking-wide">
          {product.brandName}
        </p>
        <Link to={`/product/${product._id}`} onClick={scrollTop}>
          <h3 className="font-inter font-medium text-[15px] text-ds-on-background leading-snug line-clamp-2 mb-2 hover:text-ds-primary transition-colors">
            {product.productName}
          </h3>
        </Link>

        <StarRating
          rating={product.averageRating}
          count={product.reviewCount}
        />

        {/* Price: strikethrough originalPrice + SALE label + sellingPrice */}
        <div className="flex items-center gap-2 flex-wrap">
          {onSale ? (
            <>
              <span className="font-inter text-ds-secondary line-through text-[14px]">
                {displayNPRCurrency(product.price)}
              </span>
              {/* RULE 6: Sale badge is text label "SALE", never color alone */}
              <span className="font-inter font-bold text-ds-error text-[12px] uppercase">
                SALE
              </span>
              <span className="font-inter font-semibold text-[16px] text-ds-on-background">
                {displayNPRCurrency(product.sellingPrice)}
              </span>
            </>
          ) : (
            <span className="font-inter font-semibold text-[16px] text-ds-on-background">
              {displayNPRCurrency(product.sellingPrice)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCardNew;
